import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { ClipboardList, ArrowRight, AlertCircle, CheckCircle2 } from "lucide-react";

export default function TeacherAttempts() {
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | pending

  useEffect(() => {
    api
      .get("/teacher/attempts")
      .then((r) => setAttempts(r.data))
      .finally(() => setLoading(false));
  }, []);

  const filtered = attempts.filter((a) => (filter === "pending" ? a.has_pending_grading : true));

  return (
    <AppShell title="Attempts & grading" subtitle="Review student submissions and grade descriptive answers.">
      <div className="flex items-center gap-2 mb-4" data-testid="attempts-filter">
        <button
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
            filter === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
          }`}
          onClick={() => setFilter("all")}
          data-testid="filter-all"
        >
          All ({attempts.length})
        </button>
        <button
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
            filter === "pending" ? "bg-amber-500 text-white border-amber-500" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
          }`}
          onClick={() => setFilter("pending")}
          data-testid="filter-pending"
        >
          Needs grading ({attempts.filter((a) => a.has_pending_grading).length})
        </button>
      </div>

      <div className="qs-card overflow-hidden" data-testid="teacher-attempts-table">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left px-4 py-3 font-bold">Student</th>
                <th className="text-left px-4 py-3 font-bold">Exam</th>
                <th className="text-left px-4 py-3 font-bold">Score</th>
                <th className="text-left px-4 py-3 font-bold">Status</th>
                <th className="text-left px-4 py-3 font-bold">Submitted</th>
                <th className="text-right px-4 py-3 font-bold">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr>
                  <td colSpan={6} className="text-center py-10 text-slate-500" data-testid="attempts-loading">Loading…</td>
                </tr>
              )}
              {!loading && filtered.map((a) => (
                <tr key={a.id} className="hover:bg-slate-50" data-testid={`teacher-attempt-row-${a.id}`}>
                  <td className="px-4 py-3 font-semibold text-slate-800">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-indigo-600 text-white grid place-items-center text-xs font-bold">
                        {a.student_name?.[0]?.toUpperCase()}
                      </div>
                      {a.student_name}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{a.exam_title}</td>
                  <td className="px-4 py-3 text-slate-700">
                    {a.score_percent}% ({a.score}/{a.total_points})
                  </td>
                  <td className="px-4 py-3">
                    {a.has_pending_grading ? (
                      <span className="inline-flex items-center gap-1 text-amber-700 text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-50">
                        <AlertCircle className="w-3.5 h-3.5" /> Needs grading
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Fully graded
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-500">{a.submitted_at ? new Date(a.submitted_at).toLocaleString() : "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/teacher/attempts/${a.id}`}
                      className="text-indigo-600 font-semibold text-sm inline-flex items-center gap-1"
                      data-testid={`review-attempt-${a.id}`}
                    >
                      Review <ArrowRight className="w-4 h-4" />
                    </Link>
                  </td>
                </tr>
              ))}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-slate-500" data-testid="no-teacher-attempts">
                    <ClipboardList className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                    No attempts match this filter yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AppShell>
  );
}
